import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Input from '../../../../shared/_TBComponents/Input';

// Multi-select tag filter. The shared Dropdown is single-select, so this is a
// small custom popover built on the same outside-click pattern as useActionMenu.

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid #D6D6D6;
  border-radius: 4px;
  background: #FFF;
  color: #1A1A1A;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: #1787FB;
  color: #FFF;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`;

/* Right-anchored when the trigger sits at the end of a narrow row, so the
   popover opens inward instead of off the edge. */
const Popover = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: ${(props) => (props.alignRight ? 'auto' : 0)};
  right: ${(props) => (props.alignRight ? 0 : 'auto')};
  width: 260px;
  max-width: calc(100vw - 64px);
  background: #FFF;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 10;
  display: flex;
  flex-direction: column;
`;

const SearchArea = styled.div`
  padding: 10px 10px 6px;
`;

const List = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding: 4px 0;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 5px 14px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 14px;
  color: #1A1A1A;

  &:hover {
    background: #F7F7F7;
  }
`;

const OptionCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #444444;
  flex-shrink: 0;
`;

const EmptyText = styled.div`
  padding: 12px 14px;
  font-family: "Nunito Sans";
  font-size: 13px;
  color: #9E9E9E;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 12px;
  border-top: 1px solid #EEEEEE;
`;

const ClearLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => (props.disabled ? '#CCCCCC' : '#77A7FF')};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
`;

const TagFilterDropdown = ({
  tags, selectedIds, onChange, alignRight,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [isOpen]);

  const sortedTags = useMemo(
    () => [...(tags || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [tags],
  );

  const visibleTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTags;
    return sortedTags.filter((t) => (t.name || '').toLowerCase().includes(q));
  }, [sortedTags, query]);

  const toggleTag = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((i) => i !== id));
    else onChange([...selectedIds, id]);
  };

  return (
    <Wrapper ref={wrapperRef}>
      <TriggerButton type='button' onClick={() => setIsOpen((prev) => !prev)}>
        <Icon icon='tag' size={14} color='#1A1A1A' />
        Tags
        {selectedIds.length > 0 && <CountBadge>{selectedIds.length}</CountBadge>}
      </TriggerButton>
      {isOpen && (
        <Popover alignRight={alignRight}>
          <SearchArea>
            <Input
              name='tag-filter-search'
              inputOnly
              placeholder='Search tags'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              styledPropsInput={{ width: '100%', height: '34px', fontSize: '13px' }}
              stylePropsWrapper={{ border: '1px solid #D6D6D6', borderRadius: '4px' }}
            />
          </SearchArea>
          <List>
            {visibleTags.length === 0 ? (
              <EmptyText>No tags found.</EmptyText>
            ) : visibleTags.map((t) => (
              <OptionLabel key={t.id}>
                <OptionCheckbox
                  type='checkbox'
                  checked={selectedIds.includes(t.id)}
                  onChange={() => toggleTag(t.id)}
                />
                {t.name}
              </OptionLabel>
            ))}
          </List>
          <Footer>
            <ClearLink
              type='button'
              disabled={selectedIds.length === 0}
              onClick={() => selectedIds.length > 0 && onChange([])}
            >
              Clear
            </ClearLink>
          </Footer>
        </Popover>
      )}
    </Wrapper>
  );
};

TagFilterDropdown.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })),
  selectedIds: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  alignRight: PropTypes.bool,
};

TagFilterDropdown.defaultProps = {
  tags: [],
  selectedIds: [],
  alignRight: false,
};

export default TagFilterDropdown;
